import { DestroyRef, Injectable, inject } from '@angular/core';
import { LoggingService } from './logging.service';

/**
 * LayoutWatchdogService
 * ---------------------
 * Last-line defence against runaway layout/reflow loops (the kind that
 * froze the browser during EB-3642 development when a scroll-driven
 * height transition oscillated around its threshold).
 *
 * We use `PerformanceObserver` to listen for `layout-shift` entries and
 * `longtask` entries:
 *
 *   - LAYOUT-SHIFT BURST: If we see a sustained burst of layout shifts
 *     within a short rolling window (e.g. >= LAYOUT_SHIFT_BURST shifts
 *     in BURST_WINDOW_MS), we assume a feedback loop and engage
 *     emergency mode — adds the `cmt-emergency-stop` class to the
 *     `<html>` element.
 *
 *   - LONGTASK STORM: If the main thread is being blocked by repeated
 *     long tasks (each >= 50 ms) with very little idle time, we also
 *     engage emergency mode (catches infinite-render-loops that don't
 *     emit layout-shift events but starve the UI).
 *
 * Emergency mode (CSS in `styles.scss`):
 *   - Disables ALL CSS transitions/animations globally
 *   - Disables the `body::before` grain overlay
 *
 * Together these break any layout/transition feedback loop without
 * requiring a page reload. The user can refresh once we surface the
 * incident via a toast.
 *
 * Counters reset every BURST_WINDOW_MS so brief, legitimate bursts
 * (theme switch, expand/collapse animations) don't trip the watchdog.
 */
@Injectable({ providedIn: 'root' })
export class LayoutWatchdogService {
  /** Number of layout-shift entries in BURST_WINDOW_MS that trips the watchdog. */
  private static readonly LAYOUT_SHIFT_BURST = 12;

  /** Cumulative layout-shift score in BURST_WINDOW_MS that trips the watchdog. */
  private static readonly LAYOUT_SHIFT_SCORE_THRESHOLD = 2.0;

  /** Number of consecutive longtasks (>=50 ms) in BURST_WINDOW_MS that trips the watchdog. */
  private static readonly LONGTASK_BURST = 8;

  /** Rolling window for burst counting. */
  private static readonly BURST_WINDOW_MS = 1000;

  private readonly loggingService = inject(LoggingService);

  private shiftCount = 0;
  private shiftScoreSum = 0;
  private longTaskCount = 0;
  private windowStart = 0;
  private engaged = false;
  private observer?: PerformanceObserver;

  /**
   * Optional callback fired the first (and only) time emergency mode
   * engages. Set this from the App component to surface a toast or log
   * to Graylog. Stays a callback (not an Observable / signal) so the
   * service is independent of UI primitives.
   */
  onEmergency?: (reason: string) => void;

  /** Bootstrap the watchdog. Call once from App. Idempotent. */
  start(): void {
    if (typeof window === 'undefined' || this.observer) return;
    if (typeof PerformanceObserver === 'undefined') return;

    this.windowStart = performance.now();
    try {
      this.observer = new PerformanceObserver((list) => this.onEntries(list));
      // Both entry types are observed in a single observer where supported.
      const types: string[] = [];
      const supported = (PerformanceObserver as unknown as { supportedEntryTypes?: string[] })
        .supportedEntryTypes;
      if (supported?.includes('layout-shift')) types.push('layout-shift');
      if (supported?.includes('longtask')) types.push('longtask');
      if (types.length === 0) return;
      // `entryTypes` is the multi-type form; fall back gracefully.
      this.observer.observe({ entryTypes: types as never });
    } catch {
      // PerformanceObserver setup failed — silently no-op. Watchdog is
      // a defence-in-depth, not a hard requirement.
      this.observer = undefined;
    }

    // Optionally hook into ResizeObserver loop errors which Chrome surfaces
    // as a window error event. Treat them as immediate emergencies.
    window.addEventListener('error', this.onWindowError);
    inject(DestroyRef).onDestroy(() => this.stop());
  }

  stop(): void {
    this.observer?.disconnect();
    this.observer = undefined;
    window.removeEventListener('error', this.onWindowError);
  }

  /** Manual trigger — exposed for tests and dev-mode panic button. */
  forceEmergency(reason = 'manual'): void {
    this.engage(reason);
  }

  private onEntries(list: PerformanceObserverEntryList): void {
    if (this.engaged) return;
    const now = performance.now();
    if (now - this.windowStart > LayoutWatchdogService.BURST_WINDOW_MS) {
      // Slide the window — drop counters from the previous window.
      this.windowStart = now;
      this.shiftCount = 0;
      this.shiftScoreSum = 0;
      this.longTaskCount = 0;
    }

    for (const entry of list.getEntries()) {
      if (entry.entryType === 'layout-shift') {
        // `value` is non-standard but exposed by Chromium (LayoutShift interface).
        const value = (entry as unknown as { value?: number; hadRecentInput?: boolean });
        if (value.hadRecentInput) continue; // user-initiated shifts are fine
        this.shiftCount++;
        this.shiftScoreSum += value.value ?? 0;
      } else if (entry.entryType === 'longtask') {
        this.longTaskCount++;
      }
    }

    // Only trip on a real layout-shift score. Pure count-based detection
    // produced false positives for benign animations that emit many
    // entries with `value === 0` (e.g. the penguin easter egg's footprint
    // fade-out). Real feedback loops always accumulate non-trivial score.
    if (this.shiftScoreSum >= LayoutWatchdogService.LAYOUT_SHIFT_SCORE_THRESHOLD) {
      this.engage(
        `layout-shift burst (count=${this.shiftCount}, score=${this.shiftScoreSum.toFixed(2)})`,
      );
    } else if (
      // Both count AND score together still indicate a runaway: many
      // shifts whose individual scores summed to a meaningful fraction.
      this.shiftCount >= LayoutWatchdogService.LAYOUT_SHIFT_BURST &&
      this.shiftScoreSum >= 0.5
    ) {
      this.engage(
        `layout-shift burst (count=${this.shiftCount}, score=${this.shiftScoreSum.toFixed(2)})`,
      );
    } else if (this.longTaskCount >= LayoutWatchdogService.LONGTASK_BURST) {
      this.engage(`longtask storm (count=${this.longTaskCount})`);
    }
  }

  private onWindowError = (ev: ErrorEvent): void => {
    // Chrome surfaces ResizeObserver feedback loops here.
    if (ev.message?.includes('ResizeObserver loop')) {
      this.engage('ResizeObserver loop');
    }
  };

  private engage(reason: string): void {
    if (this.engaged) return;
    this.engaged = true;
    document.documentElement.classList.add('cmt-emergency-stop');
    // EB-3798: best-effort structured signal. The optional `onEmergency`
    // callback (set from App) is the proper hook for a user-facing toast.
    this.loggingService.warn('layout.emergency', { full_message: reason });
    this.onEmergency?.(reason);
  }
}
